package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLTextAreaElement

/**
 * iOS-style share glyph: a rounded rectangle with an open top and an
 * up-arrow rising through it. Rendered as inline SVG, used for both
 * the flight and airport share buttons. `stroke="currentColor"` so it
 * picks up the button's text colour; 18px to sit visually level with
 * the surrounding Unicode-glyph icon buttons (★ / ✕ / ↗).
 *
 * Kept as a raw markup string so callers can drop it straight into
 * `element.innerHTML` -- the whole SVG is self-contained static
 * markup, so there's no XSS risk despite the innerHTML assignment.
 *
 * Box path: starts at the top-left of the open gap (5, 11), goes
 * down, rounds the bottom-left corner, across the bottom, rounds the
 * bottom-right corner, up to the top-right of the gap. The two
 * rounded-corner arcs match the reference (line-art) share icon on
 * iOS / Material, avoiding a pure-rectangle look.
 */
internal const val SHARE_ICON_SVG: String =
    "<svg viewBox=\"0 0 24 24\" width=\"18\" height=\"18\" fill=\"none\" " +
        "stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" " +
        "stroke-linejoin=\"round\" aria-hidden=\"true\">" +
        "<path d=\"M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8\"/>" +  // rounded box, open top
        "<line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"15\"/>" +            // arrow shaft
        "<polyline points=\"8 7 12 3 16 7\"/>" +                      // arrow head (chevron)
        "</svg>"

/**
 * Offer a deep-link URL via the Web Share API (mobile native sheet,
 * desktop-browser share targets where supported), falling back to the
 * async Clipboard API, then a legacy textarea + `execCommand("copy")` as
 * a last resort. Shows a transient status-bar confirmation in every
 * branch so the user knows the share took effect.
 *
 * Shared by flight and airport share buttons -- any other deep-link
 * target can reuse this the same way.
 */
internal fun shareUrl(
    app: WebAppContainer,
    url: String,
    title: String,
    text: String,
) {
    val nav = window.navigator.asDynamic()
    val canShare = nav.share != null
    if (canShare) {
        // navigator.share returns a Promise. Success: user picked a target
        // or dismissed the sheet. Failure: permission denied or API rejected.
        // We treat dismissal as "no-op" (no status flash) so the UI isn't
        // noisy. Explicit rejection falls through to the clipboard path.
        try {
            val promise = nav.share(
                js("({})").also { opts ->
                    opts.title = title
                    opts.text = text
                    opts.url = url
                },
            )
            promise.asDynamic().then(
                { _: dynamic ->
                    app.appState.setStatusMessage("Shared")
                    window.setTimeout({
                        if (app.appState.statusMessage.value == "Shared") {
                            app.appState.setStatusMessage("")
                        }
                    }, 2500)
                    Unit
                },
                { _: dynamic ->
                    copyToClipboardWithFeedback(app, url)
                    Unit
                },
            )
            return
        } catch (_: Throwable) {
            // navigator.share threw synchronously (rare) -- fall through.
        }
    }
    copyToClipboardWithFeedback(app, url)
}

private fun copyToClipboardWithFeedback(app: WebAppContainer, url: String) {
    val clipboard = window.navigator.asDynamic().clipboard
    val doneMsg = "Link copied: $url"
    if (clipboard != null) {
        try {
            val promise = clipboard.writeText(url)
            promise.asDynamic().then(
                { _: dynamic ->
                    app.appState.setStatusMessage(doneMsg)
                    clearStatusAfter(app, doneMsg, 4_000)
                    Unit
                },
                { _: dynamic ->
                    app.appState.setStatusMessage("Share link: $url")
                    Unit
                },
            )
            return
        } catch (_: Throwable) {
            // Fall through to legacy copy.
        }
    }
    // Legacy fallback -- works on older browsers that have neither
    // Web Share API nor the async Clipboard API (uncommon on modern mobile).
    val textarea = document.createElement("textarea") as HTMLTextAreaElement
    textarea.value = url
    textarea.style.asDynamic().position = "fixed"
    textarea.style.asDynamic().opacity = "0"
    document.body?.appendChild(textarea)
    textarea.select()
    val success = try {
        document.asDynamic().execCommand("copy") as? Boolean ?: false
    } catch (_: Throwable) { false }
    document.body?.removeChild(textarea)
    if (success) {
        app.appState.setStatusMessage(doneMsg)
        clearStatusAfter(app, doneMsg, 4_000)
    } else {
        app.appState.setStatusMessage("Share link: $url")
    }
}

/**
 * Clear a specific status message after [ms] if nothing else has replaced
 * it in the meantime. The identity check prevents us from clobbering a
 * later unrelated status (e.g. a server error) that landed in between.
 */
private fun clearStatusAfter(app: WebAppContainer, msg: String, ms: Int) {
    window.setTimeout({
        if (app.appState.statusMessage.value == msg) {
            app.appState.setStatusMessage("")
        }
    }, ms)
}
